/**
 * VIEWPORT entity → `RenderableEntity` adapter (PR-D).
 *
 * AutoCAD's paper-space VIEWPORT is a rectangular (or XCLIP-clipped
 * polygonal) window on top of model-space geometry. The 3D renderer
 * draws:
 *   1. A line-pipeline border (handled by the existing PR-B path that
 *      already converts the viewport rect to four line segments).
 *   2. A stencil-clipping pass for the contained model-space children
 *      (see `viewports/viewportClipping.ts`).
 *
 * This adapter only emits the viewport `RenderableEntity`. The
 * stencil clip path is computed lazily by the renderer when it walks
 * the viewport's `clipPath` (XCLIP polygon) or rotated rectangle.
 *
 * # Properties read
 *
 *   - `Center X` / `Center Y` / `Width` / `Height` — viewport rect
 *     in paper-space (already exposed by buildViewportPreview).
 *   - `Rotation` — optional, rotates the rectangle around centre.
 *   - `Clip path` — optional JSON-encoded `Vec3[]` for XCLIP.
 *   - `Child IDs` — optional JSON-encoded id list for renderer
 *     hit-testing into the clipped scene; empty for now (PR-D scope
 *     covers the clipping primitive, child enumeration is wired by
 *     PR-G's draft preview path).
 *
 * # Edge cases
 *
 *   - 0-area viewport → adapter returns null (renderer skips).
 *   - Clip path with < 3 vertices → fallback to rectangular clip.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { applyOffset } from "../coordinateOrigin";
import { readNumber } from "../../scene/renderHelpers";
import type {
  RenderableEntity,
  SceneGeometry,
  Vec3,
} from "../types";

import {
  buildRenderableShell,
  boundsFromPoints,
  type AdapterContext,
} from "./adapterContext";

export function viewportToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity | null {
  const cx = readNumber(entity.properties, "Center X");
  const cy = readNumber(entity.properties, "Center Y");
  const w = readNumber(entity.properties, "Width");
  const h = readNumber(entity.properties, "Height");
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
  if (!(w > 0) || !(h > 0)) return null;

  const rotationDeg = readNumber(entity.properties, "Rotation", 0);
  const rotation = (rotationDeg * Math.PI) / 180;

  // Construct the rectangle in paper-space, then rotate around the
  // centre. The renderer applies the same rotation to the stencil
  // tessellation so the child clip and border line up exactly.
  const halfW = w / 2;
  const halfH = h / 2;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const rotatePoint = (lx: number, ly: number): Vec3 =>
    applyOffset({
      x: cx + lx * cos - ly * sin,
      y: cy + lx * sin + ly * cos,
      z: 0,
    });
  const rect: Vec3[] = [
    rotatePoint(-halfW, -halfH),
    rotatePoint(halfW, -halfH),
    rotatePoint(halfW, halfH),
    rotatePoint(-halfW, halfH),
  ];

  // Optional XCLIP polygon — when present overrides the rectangular
  // clip path. Stored as JSON array of `{x, y}` in the property bag.
  let clipPath: Vec3[] | undefined;
  const rawClip = entity.properties["__viewport_clip_path"];
  if (rawClip) {
    try {
      const parsed = JSON.parse(rawClip);
      if (Array.isArray(parsed) && parsed.length >= 3) {
        clipPath = parsed
          .map((p) => {
            if (typeof p !== "object" || p === null) return null;
            const obj = p as { x?: unknown; y?: unknown; z?: unknown };
            const x = Number(obj.x);
            const y = Number(obj.y);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
            const z = Number.isFinite(Number(obj.z)) ? Number(obj.z) : 0;
            return applyOffset({ x, y, z });
          })
          .filter((p): p is Vec3 => p !== null);
        if (clipPath.length < 3) clipPath = undefined;
      }
    } catch {
      clipPath = undefined;
    }
  }

  const childIds: string[] = [];
  const rawChildren = entity.properties["__viewport_child_ids"];
  if (rawChildren) {
    try {
      const parsed = JSON.parse(rawChildren);
      if (Array.isArray(parsed)) {
        for (const id of parsed) {
          if (typeof id === "string") childIds.push(id);
        }
      }
    } catch {
      /* ignore */
    }
  }

  const geometry: SceneGeometry = {
    kind: "viewport",
    rect,
    ...(clipPath ? { clipPath } : {}),
    childIds,
    rotation,
  };
  const bounds = boundsFromPoints(rect);

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
